import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddOutlined from '@mui/icons-material/AddOutlined';
import {
  ContextSelectPanel,
  ContextSelectRow,
  ContextSelectSection,
  EmptyState,
  StatusPill,
} from '../ui';
import { AuthContext } from '../context/auth-context';
import { bidActions, toBidSnapshot, writeStoredBid } from '../../store/bid';
import { STATUS_GROUPS, statusLabel, statusTone } from '../../utils/bidStatus';
import BidFormDialog from './BidFormDialog';
import { useBidList } from './useBids';
import { useDelayedFlag } from './useDelayedFlag';

const RECENT_CLOSED_COUNT = 5;
const SKELETON_ROWS = 4;

const matchesSearch = (bid, query) =>
  !query ||
  bid.name.toLowerCase().includes(query) ||
  bid.bidNumber.toLowerCase().includes(query);

const isOpenBid = (bid) => STATUS_GROUPS.open.includes(bid.status);

const byUpdatedDesc = (a, b) =>
  new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();

const RowsSkeleton = () => {
  const { ft } = useTheme().palette;
  const bar = {
    bgcolor: ft.neutrals.borderLight,
    borderRadius: `${ft.radius.control}px`,
    transform: 'none',
  };
  return (
    <Box data-testid="bid-panel-skeleton" sx={{ px: 3, py: 1 }}>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <Box
          key={i}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25 }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            <Skeleton
              variant="rectangular"
              animation="wave"
              height={12}
              width="70%"
              sx={bar}
            />
            <Skeleton
              variant="rectangular"
              animation="wave"
              height={10}
              width={72}
              sx={bar}
            />
          </Box>
          <Skeleton
            variant="rectangular"
            animation="wave"
            height={22}
            width={76}
            sx={{ ...bar, borderRadius: `${ft.radius.pill}px` }}
          />
        </Box>
      ))}
    </Box>
  );
};

/**
 * BidSelectPanel — the Select bid panel opened from the sidebar bid block
 * (ticket #295, plan §4.8): open bids, the five most recently closed, search
 * across every bid, and New bid. Open state lives in state.bid.isPanelOpen.
 */
const BidSelectPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ft } = useTheme().palette;
  const { userId } = useContext(AuthContext);
  const isOpen = useSelector((state) => state.bid.isPanelOpen);
  const selectedBid = useSelector((state) => state.bid.selectedBid);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Becomes true on the first keystroke: from then on search runs over every
  // bid, so an older closed bid can be found too.
  const [searchAll, setSearchAll] = useState(false);

  // Fresh lists each time the panel opens.
  const openList = useBidList({ group: 'open', enabled: isOpen });
  const wonList = useBidList({
    group: 'won',
    limit: RECENT_CLOSED_COUNT,
    enabled: isOpen,
  });
  const closedList = useBidList({
    group: 'closed',
    limit: RECENT_CLOSED_COUNT,
    enabled: isOpen,
  });
  const allList = useBidList({ group: 'all', enabled: isOpen && searchAll });

  // Reset the search each time the panel closes.
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSearchAll(false);
    }
  }, [isOpen]);

  const query = search.trim().toLowerCase();

  const { openBids, closedBids } = useMemo(() => {
    if (searchAll && query) {
      const matches = allList.bids.filter((bid) => matchesSearch(bid, query));
      return {
        openBids: matches.filter(isOpenBid),
        closedBids: matches
          .filter((bid) => !isOpenBid(bid))
          .sort(byUpdatedDesc),
      };
    }
    const recentClosed = [...wonList.bids, ...closedList.bids]
      .sort(byUpdatedDesc)
      .slice(0, RECENT_CLOSED_COUNT);
    return {
      openBids: openList.bids.filter((bid) => matchesSearch(bid, query)),
      closedBids: recentClosed.filter((bid) => matchesSearch(bid, query)),
    };
  }, [
    searchAll,
    query,
    allList.bids,
    openList.bids,
    wonList.bids,
    closedList.bids,
  ]);

  const isSearching = searchAll && !!query;
  const lists = isSearching ? [allList] : [openList, wonList, closedList];
  const isLoading = lists.some((list) => list.isLoading);
  const error = lists.find((list) => list.error)?.error;
  const showSkeleton = useDelayedFlag(isOpen && isLoading);

  const handleClose = () => dispatch(bidActions.closePanel());

  const handleSearchChange = (text) => {
    setSearch(text);
    if (text.trim()) setSearchAll(true);
  };

  const handleSelect = (bid) => {
    const snapshot = toBidSnapshot(bid);
    dispatch(bidActions.selectBid(snapshot));
    writeStoredBid(userId, snapshot);
    handleClose();
    navigate('/bids/overview');
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/bids');
  };

  const handleNewBid = () => {
    handleClose();
    setIsCreateOpen(true);
  };

  const handleRetry = () => lists.forEach((list) => list.reload());

  const newBidButton = (variant) => (
    <Button
      variant={variant}
      startIcon={<AddOutlined />}
      onClick={handleNewBid}
    >
      New bid
    </Button>
  );

  const renderRows = (bids) =>
    bids.map((bid) => (
      <ContextSelectRow
        key={bid.id}
        primary={bid.name}
        secondary={bid.bidNumber}
        end={
          <StatusPill
            tone={statusTone(bid.status)}
            label={statusLabel(bid.status)}
          />
        }
        selected={bid.id === selectedBid?.id}
        onClick={() => handleSelect(bid)}
      />
    ));

  const renderBody = () => {
    if (error) {
      return (
        <EmptyState
          compact
          title="The bids could not be loaded."
          action={
            <Button variant="outlined" onClick={handleRetry}>
              Try again
            </Button>
          }
        />
      );
    }
    if (isLoading) return showSkeleton ? <RowsSkeleton /> : null;
    if (openBids.length === 0 && closedBids.length === 0) {
      return query ? (
        <EmptyState compact title="No bids match your search." />
      ) : (
        <EmptyState
          compact
          title="No bids yet"
          action={newBidButton('contained')}
        />
      );
    }
    return (
      <>
        {openBids.length > 0 && (
          <>
            <ContextSelectSection>Open bids</ContextSelectSection>
            {renderRows(openBids)}
          </>
        )}
        {closedBids.length > 0 && (
          <>
            <ContextSelectSection>
              {isSearching ? 'Closed bids' : 'Recently closed'}
            </ContextSelectSection>
            {renderRows(closedBids)}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <ContextSelectPanel
        open={isOpen}
        onClose={handleClose}
        title="Select bid"
        search={search}
        onSearchChange={handleSearchChange}
        searchPlaceholder="Search by name or bid number"
        searchAriaLabel="Search bids"
        footer={
          <>
            <Button
              variant="text"
              onClick={handleViewAll}
              sx={{ color: ft.neutrals.textBody }}
            >
              View all bids
            </Button>
            {newBidButton('outlined')}
          </>
        }
      >
        {renderBody()}
      </ContextSelectPanel>
      <BidFormDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />
    </>
  );
};

export default BidSelectPanel;
